import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { Router } from "@angular/router";
import { API_ROUTE } from "app/api-routes";
import { STORAGE_KEY } from "app/app-constant";
import { ApiService } from "app/service/api.service";
import { catchError, forkJoin, of, Subscription } from "rxjs";

interface RiaRecordCard {
  id?: string;
  name: string;
  subtitle: string;
  risk: string;
  status: string;
  score: string;
  route: string;
  queryParams?: Record<string, string>;
  raw?: any;
}

interface RiaMessage {
  role: "agent" | "user";
  text: string;
  time: string;
  records?: RiaRecordCard[];
}

interface RiaPortfolioContext {
  vendors: any[];
  risks: any[];
  assessments: any[];
  contracts: any[];
  tasks: any[];
  riskTiering: any[];
  dashboard: any;
  loadedAt?: Date;
}

@Component({
  selector: "quick-chat",
  templateUrl: "./quick-chat.component.html",
  styleUrls: ["./quick-chat.component.scss"],
  encapsulation: ViewEncapsulation.None,
  exportAs: "quickChat",
  standalone: true,
  imports: [],
})
export class QuickChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @HostBinding("class.quick-chat-opened") get quickChatOpened(): boolean {
    return this.isOpen;
  }

  @ViewChild("riaMessageBody") private messageBody?: ElementRef<HTMLElement>;

  isOpen = false;
  isThinking = false;
  isLoadingContext = false;
  isVendorSession = false;
  draft = "";

  readonly suggestedPrompts = [
    "How many outsourced vendors do we have?",
    "How many material vendors do we have?",
    "Who has the longest due assessment pending?",
    "Which third parties have critical financial risk?",
  ];

  messages: RiaMessage[] = [];

  get hasChatStarted(): boolean {
    return this.messages.some((message) => message.role === "user");
  }

  private context: RiaPortfolioContext = this.getEmptyContext();
  private shouldScrollToLatest = false;
  private contextSubscription?: Subscription;

  constructor(
    private readonly apiService: ApiService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isVendorSession = this.isCurrentSessionVendor();
    if (this.isVendorSession) {
      this.isOpen = false;
      this.isLoadingContext = false;
      this.context = this.getEmptyContext();
      return;
    }

    this.resetMessages();
    this.loadPortfolioContext();
  }

  ngOnDestroy(): void {
    this.contextSubscription?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (!this.shouldScrollToLatest || !this.messageBody?.nativeElement) {
      return;
    }
    const body = this.messageBody.nativeElement;
    body.scrollTo({ top: body.scrollHeight, behavior: "smooth" });
    this.shouldScrollToLatest = false;
  }

  toggleChat(): void {
    if (this.isVendorSession) {
      this.isOpen = false;
      return;
    }

    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.context.loadedAt) {
      this.loadPortfolioContext();
    }
  }

  closeChat(): void {
    this.isOpen = false;
  }

  resetChat(): void {
    if (this.isVendorSession) {
      this.isOpen = false;
      return;
    }

    this.resetMessages();
    this.loadPortfolioContext();
  }

  submitMessage(event: Event): void {
    event.preventDefault();
    if (this.isVendorSession) {
      this.isOpen = false;
      return;
    }

    this.ask(this.draft);
  }

  ask(question: string): void {
    if (this.isVendorSession) {
      this.isOpen = false;
      return;
    }

    const cleanQuestion = (question || "").trim();
    if (!cleanQuestion || this.isThinking) {
      return;
    }

    this.messages = [
      ...this.messages,
      { role: "user", text: cleanQuestion, time: this.getTimeLabel() },
    ];
    this.draft = "";
    this.isThinking = true;
    this.queueScrollToLatest();

    window.setTimeout(() => {
      const answer = this.buildAnswer(cleanQuestion);
      this.messages = [...this.messages, answer];
      this.isThinking = false;
      this.queueScrollToLatest();
    }, 320);
  }

  openRecord(record: RiaRecordCard): void {
    this.router.navigate([record.route], { queryParams: record.queryParams || {} });
    this.isOpen = false;
  }

  explainRecord(record: RiaRecordCard): void {
    const raw = record.raw || {};
    const owner = this.getFirstValue(raw, [
      "owner",
      "ownerName",
      "relationshipOwner",
      "businessOwner",
      "vendorOwner",
      "spocName",
    ]);
    const industry = this.getIndustry(raw);
    const recommendation = this.getRecommendationForRecord(record);

    this.messages = [
      ...this.messages,
      {
        role: "agent",
        text: `${record.name} is currently shown as ${record.risk} with status ${record.status}. ${industry !== "Not classified" ? `The industry context is ${industry}. ` : ""}${owner !== "Not assigned" ? `The accountable owner appears to be ${owner}. ` : ""}${recommendation}`,
        time: this.getTimeLabel(),
        records: [record],
      },
    ];
    this.queueScrollToLatest();
  }

  private loadPortfolioContext(): void {
    if (this.isVendorSession) {
      this.isLoadingContext = false;
      this.context = this.getEmptyContext();
      return;
    }

    this.isLoadingContext = true;
    this.contextSubscription?.unsubscribe();
    this.contextSubscription = forkJoin({
      dashboard: this.safeGet(API_ROUTE.DASHBOARD.root),
      vendors: this.safeGet(API_ROUTE.VENDOR_MANAGEMENT.getAll, {
        page: 1,
        limit: 75,
      }),
      risks: this.safeGet(API_ROUTE.RISK_MANAGEMENT.getAll, {
        page: 1,
        limit: 40,
      }),
      assessments: this.safeGet(API_ROUTE.ASSESSMENT.getAll, {
        page: 1,
        limit: 40,
      }),
      contracts: this.safeGet(API_ROUTE.CONTRACT.getAll, {
        page: 1,
        limit: 40,
      }),
      tasks: this.safeGet(API_ROUTE.MY_TASK.getAll, { page: 1, limit: 20 }),
      riskTiering: this.safeGet(API_ROUTE.RISK_TIERING.getAll, {
        page: 1,
        limit: 35,
      }),
    }).subscribe({
      next: (responses: any) => {
        this.context = {
          dashboard: responses?.dashboard?.data || responses?.dashboard || {},
          vendors: this.extractCollection(responses?.vendors),
          risks: this.extractCollection(responses?.risks),
          assessments: this.extractCollection(responses?.assessments),
          contracts: this.extractCollection(responses?.contracts),
          tasks: this.extractCollection(responses?.tasks),
          riskTiering: this.extractCollection(responses?.riskTiering),
          loadedAt: new Date(),
        };
        this.isLoadingContext = false;
      },
      error: () => {
        this.context = this.getEmptyContext();
        this.isLoadingContext = false;
      },
    });
  }

  private safeGet(endpoint: string, params?: any) {
    return this.apiService.get(endpoint, params).pipe(catchError(() => of(null)));
  }

  private isCurrentSessionVendor(): boolean {
    const candidates = [
      localStorage.getItem(STORAGE_KEY.USER_INFO),
      localStorage.getItem(STORAGE_KEY.USER_CONFIG),
    ];

    return candidates.some((candidate) => {
      if (!candidate) {
        return false;
      }

      try {
        const parsed = JSON.parse(candidate);
        const type = String(parsed?.userType || parsed?.userRoleType || parsed?.role || "").toLowerCase();
        const roleName = String(
          parsed?.roleDetails?.name ||
            parsed?.roleDetails?.roleName ||
            parsed?.roleDetails?.roleType ||
            parsed?.roleDetails?.desc ||
            parsed?.roleDetails?.description ||
            parsed?.profile?.role ||
            ""
        ).toLowerCase();

        return (
          type === "vendor" ||
          roleName === "vendor" ||
          roleName.includes("vendor") ||
          roleName.includes("vemdor")
        );
      } catch {
        return false;
      }
    });
  }

  private buildAnswer(question: string): RiaMessage {
    const normalizedQuestion = question.toLowerCase();
    const records = this.findMatchingVendors(normalizedQuestion);

    if (this.isOutsourcedVendorQuestion(normalizedQuestion)) {
      return this.buildOutsourcedVendorAnswer();
    }

    if (this.isMaterialVendorQuestion(normalizedQuestion)) {
      return this.buildMaterialVendorAnswer();
    }

    if (this.isPendingAssessmentDueQuestion(normalizedQuestion)) {
      return this.buildLongestDueAssessmentAnswer();
    }

    if (this.isAverageRiskQuestion(normalizedQuestion)) {
      return this.buildAverageRiskAnswer();
    }

    if (this.isAssessmentQuestion(normalizedQuestion)) {
      return this.buildAssessmentAnswer();
    }

    if (this.isContractQuestion(normalizedQuestion)) {
      return this.buildContractAnswer();
    }

    if (this.isTaskQuestion(normalizedQuestion)) {
      return this.buildTaskAnswer();
    }

    if (records.length > 1) {
      return {
        role: "agent",
        text: `I found ${records.length} possible Third Party matches in the current portfolio context. Select the correct entity and I will focus the RIA knowledge pack on that record.`,
        time: this.getTimeLabel(),
        records: records.slice(0, 5),
      };
    }

    if (records.length === 1) {
      const record = records[0];
      return {
        role: "agent",
        text: `${record.name} is currently surfaced with ${record.risk} and ${record.status}. ${this.getRecommendationForRecord(record)}`,
        time: this.getTimeLabel(),
        records: [record],
      };
    }

    if (this.isHighRiskQuestion(normalizedQuestion)) {
      return this.buildHighRiskAnswer(normalizedQuestion);
    }

    return this.buildPortfolioSnapshotAnswer();
  }

  private buildHighRiskAnswer(question: string): RiaMessage {
    const highRiskRecords = this.context.vendors
      .filter((vendor) => {
        const haystack = this.flattenText(vendor).toLowerCase();
        const hasHighRisk = /critical|high|severe|red/.test(haystack);
        const topicMatch = question.includes("financial")
          ? /financial|finance|credit|revenue|cash|liquid|solvency/.test(haystack)
          : true;
        return hasHighRisk && topicMatch;
      })
      .map((vendor) => this.toVendorCard(vendor))
      .slice(0, 5);

    if (!highRiskRecords.length) {
      return {
        role: "agent",
        text: "I did not find a high-confidence critical financial-risk match in the currently loaded Third Party set. Open Vendor Inventory or Risk Summary for a wider filtered review.",
        time: this.getTimeLabel(),
      };
    }

    return {
      role: "agent",
      text: `These are the top ${highRiskRecords.length} Third Parties that match the critical or high-risk pattern in the current portfolio context. Review the source records before taking remediation action.`,
      time: this.getTimeLabel(),
      records: highRiskRecords,
    };
  }


  private buildOutsourcedVendorAnswer(): RiaMessage {
    const outsourced = this.context.vendors.filter((vendor) => this.isOutsourcedVendor(vendor));
    const records = outsourced.map((vendor) => this.toVendorCard(vendor)).slice(0, 5);
    const total = outsourced.length;
    const suffix = this.context.vendors.length
      ? ` from ${this.context.vendors.length} currently loaded Third Party records`
      : " in the currently loaded Third Party context";

    return {
      role: "agent",
      text: total
        ? `We have ${total} outsourced vendor${total === 1 ? "" : "s"}${suffix}. I am showing the first ${records.length} source record${records.length === 1 ? "" : "s"} for quick validation.`
        : "I could not identify outsourced vendors in the currently loaded portfolio context. If the outsourced flag is stored on another backend field, RIA will need that field mapped into this assistant context.",
      time: this.getTimeLabel(),
      records,
    };
  }

  private buildMaterialVendorAnswer(): RiaMessage {
    const materialVendors = this.context.vendors.filter((vendor) => this.isMaterialVendor(vendor));
    const records = materialVendors.map((vendor) => this.toVendorCard(vendor)).slice(0, 5);
    const total = materialVendors.length;
    const suffix = this.context.vendors.length
      ? ` out of ${this.context.vendors.length} currently loaded Third Party records`
      : " in the currently loaded Third Party context";

    return {
      role: "agent",
      text: total
        ? `We have ${total} material vendor${total === 1 ? "" : "s"}${suffix}. These should remain prioritized for assessment recency, resilience evidence, and open action follow-up.`
        : "I did not find records marked as material in the currently loaded Third Party context. Check whether materiality is captured after engagement submission or under a nested assessment result field.",
      time: this.getTimeLabel(),
      records,
    };
  }

  private buildLongestDueAssessmentAnswer(): RiaMessage {
    const pendingAssessments = this.context.assessments
      .map((assessment) => ({ assessment, dueDate: this.getAssessmentDueDate(assessment) }))
      .filter(({ assessment, dueDate }) => dueDate && this.isPendingAssessment(assessment))
      .sort((a, b) => (a.dueDate?.getTime() || 0) - (b.dueDate?.getTime() || 0));

    if (!pendingAssessments.length) {
      return {
        role: "agent",
        text: "I could not find a pending assessment with a readable due date in the currently loaded context. Open Assessment for the complete source list or confirm the backend due-date field mapping.",
        time: this.getTimeLabel(),
      };
    }

    const oldest = pendingAssessments[0];
    const record = this.toAssessmentCard(oldest.assessment);
    const dueLabel = oldest.dueDate?.toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

    return {
      role: "agent",
      text: `${record.name} appears to have the longest pending assessment due date in the loaded context. Due date: ${dueLabel}. Status: ${record.status}. RIA recommends escalating owner follow-up before launching new assessment work.`,
      time: this.getTimeLabel(),
      records: [record],
    };
  }

  private buildAverageRiskAnswer(): RiaMessage {
    const buckets = new Map<string, { total: number; count: number }>();

    this.context.vendors.forEach((vendor) => {
      const score = this.toNumber(
        this.getFirstValue(vendor, [
          "riskScore",
          "overallRiskScore",
          "overallScore",
          "score",
          "inherentRiskScore",
          "residualRiskScore",
        ])
      );
      if (score === null) {
        return;
      }
      const industry = this.getIndustry(vendor);
      const current = buckets.get(industry) || { total: 0, count: 0 };
      current.total += score;
      current.count += 1;
      buckets.set(industry, current);
    });

    const rows = Array.from(buckets.entries())
      .map(([industry, value]) => ({
        industry,
        average: value.total / value.count,
        count: value.count,
      }))
      .sort((a, b) => b.average - a.average)
      .slice(0, 5);

    if (!rows.length) {
      return {
        role: "agent",
        text: "The loaded vendor set does not expose numeric risk scores by industry yet. I can still help you inspect high-risk records, assessment status, contracts, and tasks.",
        time: this.getTimeLabel(),
      };
    }

    const summary = rows
      .map(
        (row) =>
          `${row.industry}: average ${row.average.toFixed(1)} across ${row.count} record${row.count === 1 ? "" : "s"}`
      )
      .join("; ");

    return {
      role: "agent",
      text: `Average risk score by industry from the currently loaded portfolio context: ${summary}. Use this as a directional RIA signal and verify the underlying records before reporting externally.`,
      time: this.getTimeLabel(),
    };
  }

  private buildAssessmentAnswer(): RiaMessage {
    const statusSummary = this.summarizeByStatus(this.context.assessments);
    const total = this.context.assessments.length;
    return {
      role: "agent",
      text: total
        ? `I found ${total} assessment records in the current portfolio context. The visible status mix is ${statusSummary}. Prioritize overdue, in-progress, and evidence-pending items before launching new assessment work.`
        : "I could not find assessment rows in the currently loaded context. Open the Assessment page to run a wider search or refresh the portfolio data.",
      time: this.getTimeLabel(),
    };
  }

  private buildContractAnswer(): RiaMessage {
    const statusSummary = this.summarizeByStatus(this.context.contracts);
    const total = this.context.contracts.length;
    return {
      role: "agent",
      text: total
        ? `I found ${total} contract records in the current portfolio context. The visible status mix is ${statusSummary}. RIA recommends checking expiring, expired, and SLA-breached contracts first because they usually create the fastest operational risk exposure.`
        : "I could not find contract rows in the currently loaded context. Open All Contracts or SLA Management for the complete source list.",
      time: this.getTimeLabel(),
    };
  }

  private buildTaskAnswer(): RiaMessage {
    const statusSummary = this.summarizeByStatus(this.context.tasks);
    const total = this.context.tasks.length;
    return {
      role: "agent",
      text: total
        ? `I found ${total} open workspace task records in the current portfolio context. The visible status mix is ${statusSummary}. Start with overdue owner actions tied to high-risk Third Parties, assessments, or contracts.`
        : "I could not find task rows in the currently loaded context. Open My Workspace to inspect live task queues.",
      time: this.getTimeLabel(),
    };
  }

  private buildPortfolioSnapshotAnswer(): RiaMessage {
    const vendorCount = this.context.vendors.length;
    const assessmentCount = this.context.assessments.length;
    const riskCount = this.context.risks.length;
    const contractCount = this.context.contracts.length;
    const taskCount = this.context.tasks.length;
    const topRecords = this.context.vendors
      .filter((vendor) => /critical|high|severe|red/.test(this.flattenText(vendor).toLowerCase()))
      .map((vendor) => this.toVendorCard(vendor))
      .slice(0, 3);

    return {
      role: "agent",
      text: `I am ready to help across the TPRM portfolio. Current context includes ${vendorCount} Third Party records, ${assessmentCount} assessments, ${riskCount} risk records, ${contractCount} contracts, and ${taskCount} task rows. Ask about high-risk vendors, industries, assessments, contracts, litigation, open actions, or portfolio trends.`,
      time: this.getTimeLabel(),
      records: topRecords,
    };
  }

  private findMatchingVendors(question: string): RiaRecordCard[] {
    const explicitIndustry = this.extractKnownIndustry(question);
    const legalIntent = /legal|litigation|lawsuit|court|regulatory/.test(question);
    const riskIntent = /critical|high|financial|risk|score|tier/.test(question);

    return this.context.vendors
      .filter((vendor) => {
        const haystack = this.flattenText(vendor).toLowerCase();
        const vendorName = this.getVendorName(vendor).toLowerCase();
        if (explicitIndustry && haystack.includes(explicitIndustry)) {
          return true;
        }
        if (legalIntent && /legal|litigation|lawsuit|court|regulatory/.test(haystack)) {
          return true;
        }
        if (riskIntent && /critical|high|financial|red|severe/.test(haystack)) {
          return true;
        }
        const meaningfulTerms = question
          .split(/[^a-z0-9]+/)
          .filter((term) => term.length > 3 && !this.stopWords.has(term));
        return meaningfulTerms.some((term) => vendorName.includes(term) || haystack.includes(term));
      })
      .map((vendor) => this.toVendorCard(vendor));
  }

  private toVendorCard(vendor: any): RiaRecordCard {
    const id = this.getFirstValue(vendor, ["_id", "id", "vendorId"]);
    return {
      id: id !== "Not assigned" ? String(id) : undefined,
      name: this.getVendorName(vendor),
      subtitle: this.getIndustry(vendor),
      risk: this.getRiskLabel(vendor),
      status: this.getStatusLabel(vendor),
      score: this.getScoreLabel(vendor),
      route: "/vendor-inventory",
      queryParams: id !== "Not assigned" ? { vendorId: String(id) } : undefined,
      raw: vendor,
    };
  }

  private toAssessmentCard(assessment: any): RiaRecordCard {
    const id = this.getFirstValue(assessment, ["_id", "id", "assessmentId"]);
    return {
      id: id !== "Not assigned" ? String(id) : undefined,
      name: this.getFirstValue(assessment, [
        "vendorName",
        "thirdPartyName",
        "vendor.vendorName",
        "vendor.name",
        "assessmentName",
        "name",
      ]),
      subtitle: "Assessment follow-up",
      risk: this.getRiskLabel(assessment),
      status: this.getStatusLabel(assessment),
      score: this.getScoreLabel(assessment),
      route: "/assessment",
      queryParams: id !== "Not assigned" ? { assessmentId: String(id) } : undefined,
      raw: assessment,
    };
  }

  private getRecommendationForRecord(record: RiaRecordCard): string {
    const risk = `${record.risk} ${record.score}`.toLowerCase();
    if (/critical|high|red|severe/.test(risk)) {
      return "Recommended next step: validate the latest assessment evidence, confirm accountable owner action, and review open risk or contract obligations before closure.";
    }
    if (/medium|moderate|amber/.test(risk)) {
      return "Recommended next step: confirm whether monitoring, reassessment, or contract renewal evidence has changed since the last review.";
    }
    return "Recommended next step: keep the record under routine monitoring and verify any pending evidence or owner actions.";
  }

  private summarizeByStatus(records: any[]): string {
    const summary = new Map<string, number>();
    records.forEach((record) => {
      const status = this.getStatusLabel(record);
      summary.set(status, (summary.get(status) || 0) + 1);
    });
    return Array.from(summary.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([status, count]) => `${status}: ${count}`)
      .join(", ") || "no visible status breakdown";
  }

  private extractCollection(response: any): any[] {
    const payload = response?.data ?? response;
    return this.firstArray(payload) || [];
  }

  private firstArray(value: any): any[] | null {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== "object") {
      return null;
    }
    const priorityKeys = [
      "docs",
      "rows",
      "records",
      "items",
      "list",
      "result",
      "results",
      "data",
      "vendors",
      "assessments",
      "risks",
      "contracts",
      "tasks",
    ];
    for (const key of priorityKeys) {
      const nested = this.firstArray(value[key]);
      if (nested) {
        return nested;
      }
    }
    for (const nestedValue of Object.values(value)) {
      const nested = this.firstArray(nestedValue);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  private getEmptyContext(): RiaPortfolioContext {
    return {
      vendors: [],
      risks: [],
      assessments: [],
      contracts: [],
      tasks: [],
      riskTiering: [],
      dashboard: {},
    };
  }

  private resetMessages(): void {
    this.messages = [
      {
        role: "agent",
        text: "Hi. I am RIA, your Risk Intelligence Agent for the TPRM portfolio. I can help with vendor counts, materiality, pending assessments, financial risk, contracts, and open actions. Choose a starter question below or ask your own.",
        time: this.getTimeLabel(),
      },
    ];
  }

  private getVendorName(vendor: any): string {
    return this.getFirstValue(vendor, [
      "vendorName",
      "thirdPartyName",
      "companyName",
      "entityName",
      "businessName",
      "name",
      "legalName",
    ]);
  }

  private getIndustry(vendor: any): string {
    return this.getFirstValue(vendor, [
      "industry",
      "vendorIndustry",
      "sector",
      "categoryName",
      "vendorCategory",
      "category.name",
      "category",
      "businessCategory",
    ]);
  }

  private getRiskLabel(record: any): string {
    return this.getFirstValue(record, [
      "riskLevel",
      "riskRating",
      "riskTier",
      "overallRisk",
      "overallRiskLevel",
      "businessRisk",
      "businessRiskLevel",
      "inherentRiskLevel",
      "residualRiskLevel",
      "criticality",
    ]);
  }

  private getScoreLabel(record: any): string {
    return this.getFirstValue(record, [
      "riskScore",
      "overallRiskScore",
      "overallScore",
      "score",
      "inherentRiskScore",
      "residualRiskScore",
    ]);
  }

  private getStatusLabel(record: any): string {
    return this.getFirstValue(record, [
      "status",
      "workflowStatus",
      "assessmentStatus",
      "contractStatus",
      "taskStatus",
      "state",
      "stage",
      "lifecycleStatus",
    ]);
  }

  private getFirstValue(source: any, paths: string[]): string {
    for (const path of paths) {
      const value = this.readPath(source, path);
      if (value !== undefined && value !== null && value !== "") {
        if (typeof value === "object") {
          const nestedName = this.readPath(value, "name") || this.readPath(value, "title");
          if (nestedName) {
            return String(nestedName);
          }
          continue;
        }
        return String(value);
      }
    }
    return "Not assigned";
  }

  private readPath(source: any, path: string): any {
    return path.split(".").reduce((value, key) => value?.[key], source);
  }

  private flattenText(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.map((entry) => this.flattenText(entry)).join(" ");
    }
    if (typeof value === "object") {
      return Object.values(value)
        .map((entry) => this.flattenText(entry))
        .join(" ");
    }
    return "";
  }

  private extractKnownIndustry(question: string): string | null {
    const industries = [
      "manufacturing",
      "technology",
      "finance",
      "financial",
      "healthcare",
      "retail",
      "logistics",
      "professional",
      "services",
      "software",
    ];
    return industries.find((industry) => question.includes(industry)) || null;
  }


  private isOutsourcedVendor(vendor: any): boolean {
    const flag = this.getFirstValue(vendor, [
      "outsourcedVendor",
      "isOutsourcedVendor",
      "outsourced",
      "isOutsourced",
      "vendorDetails.outsourcedVendor",
      "engagement.outsourcedVendor",
      "services.outsourced",
    ]);
    const haystack = this.flattenText(vendor).toLowerCase();
    return this.isAffirmative(flag) || /business process outsourcing|bpo|outsourced vendor|outsourcing partner|outsourced service/.test(haystack);
  }

  private isMaterialVendor(vendor: any): boolean {
    const flag = this.getFirstValue(vendor, [
      "isMaterialVendor",
      "materialVendor",
      "vendorDetails.materialVendor",
      "materialityRating",
      "materiality",
      "materialityResult.materiality",
      "engagement.materialityRating",
      "criticalityRating",
      "criticality",
    ]);
    const haystack = `${flag} ${this.flattenText(vendor)}`.toLowerCase();
    return this.isAffirmative(flag) || /material|material vendor|critical materiality|high materiality/.test(haystack);
  }

  private isPendingAssessment(assessment: any): boolean {
    const status = this.getStatusLabel(assessment).toLowerCase();
    return !/(complete|completed|closed|approved|cancelled|canceled|submitted)/.test(status);
  }

  private getAssessmentDueDate(assessment: any): Date | null {
    const dueValue = this.getFirstValue(assessment, [
      "dueDate",
      "assessmentDueDate",
      "targetDate",
      "targetCompletionDate",
      "expectedCompletionDate",
      "completionDueDate",
      "reviewDueDate",
      "endDate",
      "metadata.dueDate",
    ]);
    const parsed = Date.parse(dueValue);
    return Number.isNaN(parsed) ? null : new Date(parsed);
  }

  private isAffirmative(value: string): boolean {
    return /^(true|yes|y|1|material|critical|high)$/i.test(String(value || "").trim());
  }

  private queueScrollToLatest(): void {
    this.shouldScrollToLatest = true;
  }

  private toNumber(value: string): number | null {
    const match = String(value || "").match(/-?\d+(\.\d+)?/);
    return match ? Number(match[0]) : null;
  }

  private isOutsourcedVendorQuestion(question: string): boolean {
    return /outsourc|bpo|third party service provider/.test(question) && /vendor|third|part/.test(question);
  }

  private isMaterialVendorQuestion(question: string): boolean {
    return /material/.test(question) && /vendor|third|part/.test(question);
  }

  private isPendingAssessmentDueQuestion(question: string): boolean {
    return /(longest|oldest|most overdue|pending|due)/.test(question) && /assessment/.test(question);
  }

  private isAverageRiskQuestion(question: string): boolean {
    return question.includes("average") && question.includes("risk") && question.includes("industry");
  }

  private isAssessmentQuestion(question: string): boolean {
    return /assessment|evidence|questionnaire|control/.test(question);
  }

  private isContractQuestion(question: string): boolean {
    return /contract|renewal|expiry|expired|sla|performance/.test(question);
  }

  private isTaskQuestion(question: string): boolean {
    return /task|action|owner|overdue|due/.test(question);
  }

  private isHighRiskQuestion(question: string): boolean {
    return /critical|high|financial|risk|red|severe/.test(question);
  }

  private getTimeLabel(): string {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private readonly stopWords = new Set([
    "which",
    "third",
    "parties",
    "party",
    "have",
    "with",
    "show",
    "list",
    "what",
    "about",
    "your",
    "portfolio",
    "risk",
    "score",
    "status",
    "all",
    "the",
    "and",
    "for",
    "tell",
    "give",
  ]);
}
